import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import {
  GraduationCap, Wallet, Globe2, Briefcase, BookOpenCheck, Plane, Check, MapPin,
  ShieldCheck, Compass, FileCheck, Stamp, Luggage, ListChecks, Quote, ArrowRight,
  HeartHandshake, Phone, Mail, Calculator, CalendarDays, BadgeCheck, PenLine, ChevronRight,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionHead, Marquee } from "@/components/site/Widgets";
import { ContactForm } from "@/components/site/LeadForms";
import ProfileAssessment from "@/components/site/ProfileAssessment";
import { CountryCard } from "@/pages/Platform";
import { api, track, scrollToSection, WHATSAPP_URL } from "@/lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1577985043696-8bd54d9f093f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMHVuaXZlcnNpdHklMjBjYW1wdXMlMjBldXJvcGV8ZW58MHx8fHwxNzg3OTkzMDkzfDA&ixlib=rb-4.1.0&q=85",
  romania: "https://images.unsplash.com/photo-1689655451590-be38de8003e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxyb21hbmlhJTIwYnVjaGFyZXN0JTIwYXJjaGl0ZWN0dXJlJTIwZXVyb3BlfGVufDB8fHx8MTc4Nzk5MzA5M3ww&ixlib=rb-4.1.0&q=85",
  student: "https://images.unsplash.com/photo-1559823081-c8f6a1b1d792?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzdHVkZW50cyUyMHN0dWR5JTIwZ3JhZHVhdGlvbiUyMGFicm9hZHxlbnwwfHx8fDE3ODc5OTMwOTN8MA&ixlib=rb-4.1.0&q=85",
  graduation: "https://images.unsplash.com/photo-1627556704263-b486db44a463?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxzdHVkZW50cyUyMHVuaXZlcnNpdHklMjBjYW1wdXMlMjBldXJvcGV8ZW58MHx8fHwxNzg3OTkzMDkzfDA&ixlib=rb-4.1.0&q=85",
};

/* ---------------- HERO ---------------- */

const HERO_TAGS = ["UK", "USA", "CANADA", "AUSTRALIA", "GERMANY", "IRELAND", "NEW ZEALAND", "FRANCE", "ITALY", "ROMANIA"];
const TAG_POS = [
  "top-4 -left-2", "top-16 right-0", "top-1/3 -left-6", "top-1/2 right-4",
  "bottom-1/3 -left-3", "bottom-24 right-2", "bottom-8 left-6", "top-8 left-1/3",
  "bottom-14 left-1/4", "top-24 left-1/2",
];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span className="block" initial={{ y: "112%" }} animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}>
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const navigate = useNavigate();
  const trust = ["Bachelor's, Master's & PhD", "17 Study Destinations", "English-Taught Programs", "Application to Visa Support"];

  return (
    <section data-testid="hero-section" className="relative mesh-gradient bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 lg:pt-44 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 pt-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-[#EEF5FF] border border-[#1E40AF]/15 rounded-full px-4 py-2 mb-7">
              <Globe2 className="w-4 h-4 text-[#1E40AF]" />
              <span className="text-xs font-data uppercase tracking-[0.18em] text-[#1E40AF] font-semibold">Global Education. Informed Decisions. Global Futures.</span>
            </motion.div>

            <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl lg:text-[3.6rem] font-black tracking-tight leading-[1.06] text-[#172033]">
              <MaskedLine delay={0.25}>YOUR GLOBAL EDUCATION</MaskedLine>
              <MaskedLine delay={0.4}>JOURNEY STARTS</MaskedLine>
              <MaskedLine delay={0.55}><span className="font-editorial italic font-semibold text-[#B45309]">HERE.</span></MaskedLine>
            </h1>

            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-6 text-base sm:text-lg text-[#5F6B7A] leading-relaxed max-w-xl">
              Discover the right country, university and course for your academic goals — with personalized guidance
              from profile assessment to application and visa preparation.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-8 flex flex-wrap gap-4">
              <button onClick={() => scrollToSection("#assessment")} data-testid="hero-cta-assessment"
                className="px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-xl shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center gap-2">
                GET FREE PROFILE ASSESSMENT <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => navigate("/destinations")} data-testid="hero-cta-destinations"
                className="px-8 py-4 rounded-full text-sm font-bold text-[#172033] bg-white border border-[#E6EBF1] hover:border-[#1E40AF] hover:text-[#1E40AF] active:scale-[0.98] transition-all">
                EXPLORE DESTINATIONS
              </button>
            </motion.div>

            <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-9 flex flex-wrap gap-x-5 gap-y-2">
              {trust.map((t) => (
                <li key={t} className="flex items-center gap-1.5 text-xs sm:text-sm text-[#5F6B7A]">
                  <Check className="w-3.5 h-3.5 text-[#059669]" /> {t}
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div className="lg:col-span-6 relative" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 ring-1 ring-[#E6EBF1] h-[300px] sm:h-[400px]">
              <img src={IMG.hero} alt="Students on an international university campus" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent" />
            </div>
            {HERO_TAGS.map((t, i) => (
              <span key={t} style={{ animationDelay: `${i * 0.5}s` }}
                className={`hidden md:inline-flex absolute ${TAG_POS[i]} animate-floaty bg-white border border-[#E6EBF1] shadow-lg shadow-slate-900/8 text-[10px] font-data font-bold tracking-widest text-[#1E40AF] px-3 py-1.5 rounded-full`}>
                {t}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- PROFILE ASSESSMENT ---------------- */

function AssessmentSection() {
  return (
    <section id="assessment" data-testid="assessment-section" className="py-20 lg:py-24 bg-[#F7F9FC] border-y border-[#E6EBF1]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ProfileAssessment />
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- TRUST STRIP ---------------- */

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, { duration: 1.8, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setVal(Math.round(v)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function TrustStrip() {
  const items = [
    { big: <Counter to={100} suffix="%" />, label: "PERSONALISED GUIDANCE" },
    { big: "17", label: "STUDY DESTINATIONS" },
    { big: "BACHELOR'S", label: "MASTER'S & PhD" },
    { big: "END-TO-END", label: "APPLICATION SUPPORT" },
  ];
  return (
    <section data-testid="trust-metrics-strip" className="bg-white border-b border-[#E6EBF1] py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {items.map((it, i) => (
          <Reveal key={it.label} delay={i * 0.08} className="text-center">
            <p className="text-3xl sm:text-4xl font-black tracking-tight text-[#172033]">{it.big}</p>
            <p className="mt-2 text-[11px] font-data uppercase tracking-[0.25em] text-[#1E40AF]">{it.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- DESTINATION EXPLORER ---------------- */

function Destinations() {
  const [countries, setCountries] = useState([]);
  useEffect(() => { api.get("/content/countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);
  return (
    <section id="destinations" data-testid="destinations-section" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Destination Explorer" title="EXPLORE STUDY DESTINATIONS"
          sub="Compare countries on education, programs, costs, admission requirements and student experience." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {countries.map((c, i) => <CountryCard key={c.id} c={c} i={i} />)}
        </div>
        <Reveal className="text-center mt-12">
          <Link to="/study-abroad" data-testid="destinations-explore-all"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold text-[#172033] border-2 border-[#172033] hover:bg-[#172033] hover:text-white transition-all active:scale-[0.98]">
            COMPARE ALL DESTINATIONS <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- WHY STUDY ABROAD ---------------- */

function WhyEurope() {
  const cards = [
    { icon: GraduationCap, title: "Globally Recognised Education", desc: "Study at established international universities and build a globally relevant academic profile." },
    { icon: Wallet, title: "Options For Every Budget", desc: "From low-tuition European universities to premium global institutions — plan around your budget." },
    { icon: Globe2, title: "International Exposure", desc: "Experience multicultural campuses and a truly international student environment." },
    { icon: Briefcase, title: "Career-Aligned Programs", desc: "Choose programs aligned with your academic interests and long-term goals." },
    { icon: BookOpenCheck, title: "Every Study Level", desc: "Bachelor's, Master's, MBA, PhD, diplomas and pathway programs." },
    { icon: Plane, title: "A Global Experience", desc: "Study, live and grow in a new country — with structured support throughout." },
  ];
  return (
    <section id="why-europe" data-testid="why-abroad-section" className="py-24 lg:py-28 bg-[#F7F9FC] border-y border-[#E6EBF1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Why Study Abroad" title="MORE THAN A DEGREE." sub="Build your future in a global academic environment." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              whileHover={{ y: -6 }} data-testid={`why-abroad-card-${i}`}
              className="group bg-white rounded-2xl border border-[#E6EBF1] p-8 shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-[#EEF5FF] flex items-center justify-center mb-6 group-hover:bg-[#1E40AF] transition-colors duration-300">
                <c.icon className="w-6 h-6 text-[#1E40AF] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-[#172033]">{c.title}</h3>
              <p className="mt-2.5 text-sm text-[#5F6B7A] leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FEATURED DESTINATIONS CAROUSEL ---------------- */

function FeaturedDestinations() {
  const [countries, setCountries] = useState([]);
  const railRef = useRef(null);
  useEffect(() => { api.get("/content/countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);
  const featured = countries.filter((c) => ["uk", "usa", "canada", "australia", "germany", "ireland", "new-zealand", "romania"].includes(c.slug));
  const scroll = (dir) => railRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  return (
    <section data-testid="featured-destinations-section" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end justify-between gap-6">
        <SectionHead align="left" overline="Featured Destinations" title="WHERE COULD YOUR NEXT CHAPTER TAKE YOU?" />
        <div className="hidden sm:flex gap-2.5 mb-12 lg:mb-16 shrink-0">
          <button onClick={() => scroll(-1)} data-testid="featured-prev-btn" aria-label="Previous destinations"
            className="w-11 h-11 rounded-full bg-white border border-[#E6EBF1] shadow-sm flex items-center justify-center text-[#172033] hover:border-[#1E40AF] hover:text-[#1E40AF] transition-all">
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>
          <button onClick={() => scroll(1)} data-testid="featured-next-btn" aria-label="Next destinations"
            className="w-11 h-11 rounded-full bg-white border border-[#E6EBF1] shadow-sm flex items-center justify-center text-[#172033] hover:border-[#1E40AF] hover:text-[#1E40AF] transition-all">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto pl-4 sm:pl-6 lg:pl-8">
        <div ref={railRef} data-lenis-prevent className="flex gap-5 overflow-x-auto pb-6 pr-4 snap-x snap-mandatory" style={{ scrollbarWidth: "thin" }} data-testid="featured-carousel">
          {featured.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }} className="snap-start shrink-0 w-[280px] sm:w-[320px]">
              <Link to={c.slug === "romania" ? "/study-in-romania" : `/study-in-${c.slug}`} data-testid={`featured-card-${c.slug}`}
                className="block bg-white rounded-2xl border border-[#E6EBF1] shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 hover:-translate-y-1.5 transition-all duration-300 overflow-hidden group">
                <div className="relative h-44 overflow-hidden bg-gradient-to-br from-[#EEF5FF] to-[#F7F9FC]">
                  {c.image ? (
                    <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">{c.flag}</div>
                  )}
                  <span className="absolute bottom-3 left-4 bg-white/90 backdrop-blur text-xl leading-none px-2.5 py-1.5 rounded-lg shadow">{c.flag}</span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-extrabold text-[#172033] group-hover:text-[#1E40AF] transition-colors">{c.name.toUpperCase()}</h3>
                  <p className="text-xs font-data text-[#5F6B7A] mt-1.5 line-clamp-1">{c.popular_courses}</p>
                  <p className="text-sm text-[#5F6B7A] mt-2.5 leading-relaxed line-clamp-2">{c.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#1E40AF]">
                    EXPLORE <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- ROMANIA FEATURE ---------------- */

function RomaniaFeature() {
  const highlights = ["English-Taught Opportunities", "Bachelor's & Master's Programs", "European Academic Environment", "Affordable Study Options", "International Student Community"];
  return (
    <section id="romania" data-testid="romania-feature-section" className="py-16 lg:py-20 bg-[#F7F9FC] border-y border-[#E6EBF1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-[#E6EBF1] shadow-xl shadow-slate-900/5 overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="relative min-h-[300px] lg:min-h-[540px]">
              <img src={IMG.romania} alt="Romanian architecture" className="absolute inset-0 w-full h-full object-cover" />
              <span className="absolute top-5 left-5 bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-[11px] font-bold tracking-widest uppercase px-3.5 py-1.5 rounded-full">
                Featured Destination
              </span>
            </div>
            <div className="p-8 sm:p-12 lg:p-16">
              <Reveal>
                <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-4">Harwell Flagship Destination</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#172033]">STUDY IN ROMANIA 🇷🇴</h2>
                <p className="font-editorial italic text-xl text-[#B45309] mt-3">European Education. Practical Costs. Global Opportunities.</p>
                <p className="mt-5 text-[#5F6B7A] leading-relaxed text-[15px]">
                  Romania offers international students the opportunity to pursue higher education within Europe, with
                  Bachelor's and Master's programs across Medicine, Engineering, Computer Science, Business, Sciences and more.
                </p>
                <ul className="mt-7 space-y-3">
                  {highlights.map((h) => (
                    <li key={h} className="flex items-center gap-3 text-sm text-[#172033]">
                      <span className="w-5 h-5 rounded-full bg-[#EEF5FF] flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#1E40AF]" />
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link to="/study-in-romania" data-testid="romania-section-explore-btn"
                    className="px-7 py-3.5 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
                    EXPLORE ROMANIA
                  </Link>
                  <button onClick={() => scrollToSection("#contact")} data-testid="romania-section-counsellor-btn"
                    className="px-7 py-3.5 rounded-full text-sm font-bold text-[#172033] border border-[#E6EBF1] hover:border-[#1E40AF] hover:text-[#1E40AF] transition-all">
                    TALK TO A COUNSELLOR
                  </button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- COURSE FINDER TEASER ---------------- */

const COURSE_CATS = [
  "Business & Management", "Computer Science & IT", "Data Science & Analytics", "Engineering",
  "Healthcare & Life Sciences", "Finance & Economics", "Hospitality & Tourism", "Social Sciences",
];

function CourseFinderTeaser() {
  const navigate = useNavigate();
  return (
    <section data-testid="course-finder-section" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Course Finder" title="FIND YOUR COURSE"
          sub="Filter international programs by subject, country, level and budget." />
        <Reveal className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {COURSE_CATS.map((c) => (
            <button key={c} onClick={() => navigate(`/courses?category=${encodeURIComponent(c.replace(" & ", " & "))}`)}
              data-testid={`course-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="px-5 py-3 rounded-full bg-white border border-[#E6EBF1] text-sm font-semibold text-[#172033] hover:border-[#1E40AF] hover:text-[#1E40AF] hover:shadow-lg transition-all">
              {c}
            </button>
          ))}
        </Reveal>
        <Reveal className="text-center mt-10">
          <Link to="/courses" data-testid="course-finder-all"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
            FIND PROGRAMS <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- UNIVERSITIES ---------------- */

export function Universities({ dark = false }) {
  const [unis, setUnis] = useState([]);
  useEffect(() => { api.get("/content/universities").then((r) => setUnis(r.data)).catch(() => {}); }, []);
  const initials = (name) => name.split(" ").filter((w) => w[0] === w[0]?.toUpperCase()).slice(0, 2).map((w) => w[0]).join("");

  return (
    <section id="universities" data-testid="universities-section" className={dark ? "" : "py-24 lg:py-28 bg-[#F7F9FC] border-y border-[#E6EBF1]"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="University Finder" title="FIND YOUR UNIVERSITY"
          sub="Discover universities matched to your academic profile and budget." dark={dark} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {unis.slice(0, 6).map((u, i) => (
            <motion.div key={u.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.07 }} whileHover={{ y: -6 }}>
              <Link to={`/universities/${u.slug}`} data-testid={`university-card-${u.id}`}
                className={`block rounded-2xl border p-8 transition-all duration-300 h-full ${
                  dark ? "bg-[#12233E] border-[#1E355B] hover:border-[#38BDF8]/50"
                       : "bg-white border-[#E6EBF1] shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40"
                }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 font-black text-lg ${dark ? "bg-[#1E40AF] text-white" : "bg-[#172033] text-white"}`}>
                    {initials(u.name)}
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold leading-snug ${dark ? "text-white" : "text-[#172033]"}`}>{u.name}</h3>
                    <p className={`text-xs font-data mt-1 flex items-center gap-1 ${dark ? "text-[#38BDF8]" : "text-slate-500"}`}>
                      <MapPin className="w-3 h-3" /> {u.city}
                    </p>
                  </div>
                </div>
                <p className={`mt-5 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-[#5F6B7A]"}`}>{u.fields}</p>
                {u.badge && <p className={`mt-4 text-xs border-t pt-4 ${dark ? "text-slate-400 border-[#1E355B]" : "text-slate-500 border-slate-100"}`}>{u.badge}</p>}
              </Link>
            </motion.div>
          ))}
        </div>
        <Reveal className="text-center mt-12">
          <Link to="/universities" data-testid="universities-view-all-btn"
            className={`inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold transition-all active:scale-[0.98] ${
              dark ? "bg-white text-[#172033] hover:bg-slate-100" : "bg-[#172033] text-white hover:bg-[#1E293B]"
            }`}>
            EXPLORE ALL UNIVERSITIES <ArrowRight className="w-4 h-4" />
          </Link>
          <p className={`text-xs mt-6 max-w-2xl mx-auto ${dark ? "text-slate-500" : "text-slate-400"}`}>
            Listings reflect universities Harwell helps students explore. They do not imply official partnership or endorsement.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- WHY HARWELL ---------------- */

function WhyHarwell() {
  const benefits = [
    { num: "01", title: "Personalised Guidance", desc: "Understand your academic background, interests and goals." },
    { num: "02", title: "Country & Course Exploration", desc: "Compare destinations and programs aligned with your profile." },
    { num: "03", title: "Application Support", desc: "Guidance throughout the application and documentation process." },
    { num: "04", title: "Transparent Information", desc: "Clear, verified information — no exaggerated promises." },
    { num: "05", title: "Visa Guidance", desc: "Structured guidance for the student visa process." },
    { num: "06", title: "Pre-Departure Support", desc: "Prepare for your journey, accommodation and arrival." },
  ];
  return (
    <section id="why-harwell" data-testid="why-harwell-section" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="The Harwell Difference" title="WHY STUDENTS CHOOSE HARWELL GLOBAL EDUCATION" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((b, i) => (
            <motion.div key={b.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} data-testid={`why-harwell-card-${i}`}
              className="bg-white rounded-2xl border border-[#E6EBF1] p-8 shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 hover:-translate-y-1 transition-all duration-300">
              <span className="font-data text-sm text-[#B45309] font-semibold tracking-widest">{b.num}</span>
              <h3 className="mt-4 text-xl font-bold text-[#172033]">{b.title}</h3>
              <p className="mt-2.5 text-sm text-[#5F6B7A] leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
        <Reveal className="mt-16 text-center">
          <p className="font-editorial italic text-2xl sm:text-3xl lg:text-4xl text-[#172033] max-w-4xl mx-auto leading-snug">
            "From your first counselling session to your arrival on campus."
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- APPLICATION PROCESS (8 STEPS) ---------------- */

function Journey() {
  const steps = [
    { icon: Compass, title: "PROFILE ASSESSMENT", desc: "Academics, interests and goals." },
    { icon: Globe2, title: "COUNTRY & COURSE SELECTION", desc: "Compare destinations honestly." },
    { icon: ListChecks, title: "UNIVERSITY SHORTLIST", desc: "Options matched to your profile." },
    { icon: PenLine, title: "DOCUMENT PREPARATION", desc: "SOPs, LORs and academic files." },
    { icon: FileCheck, title: "APPLICATION SUBMISSION", desc: "Complete, accurate, on time." },
    { icon: Stamp, title: "OFFER / ADMISSION", desc: "Receive your university decision." },
    { icon: ShieldCheck, title: "FINANCIAL & VISA PREPARATION", desc: "Funds, documentation and filing." },
    { icon: Luggage, title: "PRE-DEPARTURE SUPPORT", desc: "Travel, accommodation, arrival." },
  ];
  return (
    <section id="process" data-testid="journey-section" className="py-24 lg:py-28 bg-[#F7F9FC] border-y border-[#E6EBF1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Application Process" title="YOUR APPLICATION JOURNEY" sub="A clear, guided path — from first conversation to campus." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.09 }} data-testid={`journey-step-${i}`}
              className="bg-white rounded-2xl border border-[#E6EBF1] p-7 shadow-sm hover:shadow-lg hover:border-[#1E40AF]/40 transition-all">
              <div className="inline-flex w-12 h-12 rounded-xl bg-[#EEF5FF] items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-[#1E40AF]" />
              </div>
              <p className="font-data text-xs text-[#B45309] font-semibold tracking-widest">0{i + 1}</p>
              <h3 className="mt-1.5 text-sm font-extrabold text-[#172033] tracking-wide leading-snug">{s.title}</h3>
              <p className="mt-2 text-xs text-[#5F6B7A] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
        <Reveal className="text-center mt-14">
          <button onClick={() => scrollToSection("#contact")} data-testid="journey-start-btn"
            className="px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all">
            START YOUR APPLICATION JOURNEY
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- SCHOLARSHIPS TEASER ---------------- */

function ScholarshipsTeaser() {
  const cards = [
    { icon: BadgeCheck, title: "University & Merit Scholarships", desc: "Institutional awards for strong academic profiles." },
    { icon: Globe2, title: "Country & Government Scholarships", desc: "National schemes supporting international students." },
    { icon: Wallet, title: "Funding & Loan Guidance", desc: "Structured planning for tuition and living costs." },
  ];
  return (
    <section data-testid="scholarships-teaser" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Scholarships & Funding" title="FUND YOUR EDUCATION SMARTLY"
          sub="Understand the scholarship landscape with verified, current information." />
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1} className="bg-white rounded-2xl border border-[#E6EBF1] p-8 shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all">
              <c.icon className="w-7 h-7 text-[#B45309] mb-4" />
              <h3 className="font-bold text-[#172033]">{c.title}</h3>
              <p className="text-sm text-[#5F6B7A] mt-2">{c.desc}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/scholarships" data-testid="scholarships-teaser-link"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] transition-all">
            EXPLORE SCHOLARSHIPS <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/education-loans" data-testid="funding-teaser-link"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold text-[#172033] border-2 border-[#172033] hover:bg-[#172033] hover:text-white transition-all">
            FUNDING GUIDANCE
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- TEST PREP TEASER ---------------- */

function TestPrepTeaser() {
  const tests = ["IELTS", "PTE", "TOEFL", "GRE", "GMAT", "SAT", "ACT", "DUOLINGO"];
  return (
    <section data-testid="test-prep-teaser" className="py-24 lg:py-28 bg-[#F7F9FC] border-y border-[#E6EBF1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <SectionHead overline="Test Preparation" title="PREPARE FOR YOUR INTERNATIONAL TEST"
          sub="Know which test you need, what it covers and how to prepare." />
        <Reveal className="flex flex-wrap justify-center gap-3 -mt-4">
          {tests.map((t) => (
            <span key={t} className="px-5 py-2.5 rounded-full bg-white border border-[#E6EBF1] text-sm font-data font-bold tracking-wider text-[#1E40AF]">
              {t}
            </span>
          ))}
        </Reveal>
        <Reveal className="mt-10">
          <Link to="/test-preparation" data-testid="test-prep-link"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] transition-all">
            EXPLORE TEST PREPARATION <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- COST CALCULATOR TEASER ---------------- */

function CostCalcTeaser() {
  const navigate = useNavigate();
  return (
    <section data-testid="cost-calc-teaser" className="py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="bg-white rounded-3xl border-2 border-[#1E40AF]/20 shadow-xl shadow-blue-900/5 p-8 sm:p-12 flex flex-col lg:flex-row items-center gap-8">
          <div className="w-16 h-16 rounded-2xl bg-[#EEF5FF] flex items-center justify-center shrink-0">
            <Calculator className="w-8 h-8 text-[#1E40AF]" />
          </div>
          <div className="flex-1 text-center lg:text-left">
            <h3 className="text-2xl font-extrabold text-[#172033] tracking-tight">STUDY ABROAD COST CALCULATOR</h3>
            <p className="text-[#5F6B7A] mt-2 max-w-2xl">
              Estimate tuition, living costs, first-year budget and total program cost across 17 destinations.
            </p>
          </div>
          <button onClick={() => navigate("/cost-calculator")} data-testid="cost-calc-teaser-btn"
            className="shrink-0 px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center gap-2">
            CALCULATE MY BUDGET <ArrowRight className="w-4 h-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- STORIES ---------------- */

function Stories() {
  const [stories, setStories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/content/testimonials").then((r) => setStories(r.data)).catch(() => {}).finally(() => setLoaded(true)); }, []);

  return (
    <section id="stories" data-testid="stories-section" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Student Journeys" title="DREAMS THAT MOVE FORWARD" />
        {loaded && stories.length === 0 ? (
          <Reveal className="max-w-2xl mx-auto">
            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-12 text-center">
              <Quote className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#172033]">Student journeys coming soon</h3>
              <p className="mt-3 text-sm text-[#5F6B7A] leading-relaxed">
                We publish real student journeys only — never fabricated testimonials. Stories from Harwell students
                will appear here with their consent.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.slice(0, 3).map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08} data-testid={`story-card-${i}`}
                className="bg-white rounded-2xl border border-[#E6EBF1] p-8 shadow-sm hover:shadow-xl transition-shadow">
                <Quote className="w-7 h-7 text-[#1E40AF] mb-4" />
                <p className="text-sm text-[#5F6B7A] leading-relaxed italic">"{s.quote}"</p>
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <p className="font-bold text-[#172033] text-sm">{s.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.program} — {s.university}, {s.country}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------- EVENTS PREVIEW ---------------- */

function EventsPreview() {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { api.get("/content/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {}).finally(() => setLoaded(true)); }, []);
  if (loaded && events.length === 0) return null;
  if (events.length === 0) return null;
  return (
    <section data-testid="events-preview" className="py-24 lg:py-28 bg-[#F7F9FC] border-y border-[#E6EBF1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Events" title="GLOBAL EDUCATION EVENTS" sub="Webinars, university sessions, workshops and orientations." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e, i) => (
            <Reveal key={e.id} delay={(i % 3) * 0.08} className="bg-white rounded-2xl border border-[#E6EBF1] p-7 shadow-sm">
              <span className="text-[11px] font-data uppercase tracking-widest text-[#1E40AF] bg-[#EEF5FF] px-2.5 py-1 rounded-full">{e.date}</span>
              <h3 className="mt-3 font-bold text-[#172033]">{e.title}</h3>
              <p className="text-sm text-[#5F6B7A] mt-2">{[e.time, e.location].filter(Boolean).join(" • ")}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-10">
          <button onClick={() => navigate("/events")} data-testid="events-view-all"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold text-[#172033] border-2 border-[#172033] hover:bg-[#172033] hover:text-white transition-all">
            VIEW ALL EVENTS <ArrowRight className="w-4 h-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- PARENTS ---------------- */

function Parents() {
  const cards = [
    { icon: ShieldCheck, title: "Transparent Guidance", desc: "Clear information on universities, requirements, costs and timelines — no exaggerated promises." },
    { icon: GraduationCap, title: "Academic-Focused Recommendations", desc: "Programs shortlisted around your child's academic profile and long-term goals." },
    { icon: HeartHandshake, title: "Step-by-Step Support", desc: "From documentation to departure, families stay informed at every stage." },
  ];
  return (
    <section id="parents" data-testid="parents-section" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[360px]">
            <img src={IMG.student} alt="Student beginning their study abroad journey" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#172033]/70 via-transparent to-transparent" />
            <p className="absolute bottom-6 left-6 right-6 text-white font-editorial italic text-xl">"Your child's future deserves the right guidance."</p>
          </Reveal>
          <div>
            <Reveal>
              <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-4">For Parents</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#172033]">
                YOUR CHILD'S FUTURE DESERVES THE RIGHT GUIDANCE.
              </h2>
              <p className="mt-5 text-[#5F6B7A] leading-relaxed">
                We understand that studying abroad is a major decision for both students and parents. Our counselling
                approach focuses on helping families understand university options, academic requirements, costs,
                documentation and the overall student journey.
              </p>
            </Reveal>
            <div className="mt-8 space-y-4">
              {cards.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.08} className="flex gap-4 bg-white rounded-xl border border-[#E6EBF1] p-5">
                  <div className="w-11 h-11 rounded-lg bg-[#EEF5FF] flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-[#1E40AF]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#172033] text-sm">{c.title}</h3>
                    <p className="text-sm text-[#5F6B7A] mt-1">{c.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.25}>
              <button onClick={() => scrollToSection("#contact")} data-testid="parents-counsellor-btn"
                className="mt-8 px-8 py-3.5 rounded-full text-sm font-bold text-white bg-[#172033] hover:bg-[#1E293B] active:scale-[0.98] transition-all">
                SPEAK TO A COUNSELLOR
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */

const FAQ_CATS = ["All", "General Study Abroad", "Country Specific", "Admissions", "English Tests", "Scholarships", "Tuition", "Visa", "Accommodation", "Applications", "Intakes"];

function FAQ({ standalone = false }) {
  const [faqs, setFaqs] = useState([]);
  const [cat, setCat] = useState("All");
  useEffect(() => { api.get("/content/faqs").then((r) => setFaqs(r.data)).catch(() => {}); }, []);
  const filtered = faqs.filter((f) => cat === "All" || (f.category || "General Study Abroad") === cat);

  useEffect(() => {
    if (!standalone || faqs.length === 0) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [faqs, standalone]);

  return (
    <section id="faq" data-testid="faq-section" className="py-24 lg:py-28 bg-[#F7F9FC] border-t border-[#E6EBF1]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Questions, Answered" title="FREQUENTLY ASKED QUESTIONS" />
        {standalone && (
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="faq-category-filters">
            {FAQ_CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`faq-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${cat === c ? "bg-[#172033] text-white" : "bg-white text-[#5F6B7A] border border-[#E6EBF1] hover:border-[#1E40AF]"}`}>
                {c}
              </button>
            ))}
          </div>
        )}
        <Reveal>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {filtered.map((f, i) => (
              <AccordionItem key={f.id} value={f.id} data-testid={`faq-item-${i}`}
                className="bg-white border border-[#E6EBF1] rounded-xl px-6 data-[state=open]:border-[#1E40AF]/40 data-[state=open]:shadow-lg transition-all">
                <AccordionTrigger className="text-left text-[15px] font-bold text-[#172033] py-5 hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-[#5F6B7A] leading-relaxed pb-6">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {filtered.length === 0 && <p className="text-center text-slate-400 py-10">No questions in this category yet.</p>}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- INSIGHTS PREVIEW ---------------- */

function Insights() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/content/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {}); }, []);
  if (posts.length === 0) return null;
  return (
    <section id="insights" data-testid="insights-section" className="py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Global Education Insights" title="STUDY ABROAD INSIGHTS" sub="Honest guides on destinations, costs, visas and student life." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }} whileHover={{ y: -6 }} data-testid={`home-insight-card-${i}`}
              className="group bg-white rounded-2xl border border-[#E6EBF1] overflow-hidden shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all duration-300">
              <Link to={`/insights/${p.slug}`}>
                <div className="relative h-44 overflow-hidden bg-[#EEF5FF]">
                  {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  <span className="absolute top-4 left-4 bg-white/90 backdrop-blur text-[#172033] text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">{p.category}</span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#172033] leading-snug group-hover:text-[#1E40AF] transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-[#5F6B7A] leading-relaxed line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <Reveal className="text-center mt-12">
          <Link to="/insights" data-testid="insights-view-all-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold text-[#172033] border-2 border-[#172033] hover:bg-[#172033] hover:text-white transition-all active:scale-[0.98]">
            VIEW ALL INSIGHTS <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */

function FinalCTA() {
  return (
    <section data-testid="final-cta-section" className="py-24 lg:py-28 bg-[#F7F9FC] border-t border-[#E6EBF1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#172033] leading-[1.06]">
              READY TO PLAN YOUR <span className="font-editorial italic font-semibold text-[#B45309]">GLOBAL EDUCATION?</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#5F6B7A] max-w-xl">
              Tell us your academic profile. We'll help you explore the right countries, courses and universities.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={() => scrollToSection("#assessment")} data-testid="final-cta-assessment-btn"
                className="px-9 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-xl shadow-blue-900/20 active:scale-[0.98] transition-all">
                GET FREE PROFILE ASSESSMENT
              </button>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: "final_cta" })}
                data-testid="final-cta-whatsapp-btn"
                className="px-9 py-4 rounded-full text-sm font-bold text-white bg-[#059669] hover:bg-[#047857] active:scale-[0.98] transition-all">
                WHATSAPP AN EXPERT
              </a>
            </div>
            <p className="mt-6 text-xs font-data uppercase tracking-[0.25em] text-slate-400">
              No pressure. No complicated process. Just clear guidance.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 ring-1 ring-[#E6EBF1] min-h-[320px]">
            <img src={IMG.graduation} alt="Graduation at an international university" className="w-full h-full object-cover" />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CONTACT ---------------- */

function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-28 bg-white border-t border-[#E6EBF1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline="Get In Touch" title="LET'S PLAN YOUR NEXT STEP." />
        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <h3 className="text-xl font-extrabold text-[#172033] tracking-tight">HARWELL GLOBAL EDUCATION</h3>
            <div className="mt-6 space-y-4 text-sm text-[#5F6B7A]">
              <p className="flex gap-3">
                <MapPin className="w-5 h-5 text-[#1E40AF] shrink-0" />
                B-148, 1st Floor, B Block, Sector 63, Noida, Uttar Pradesh - 201301
              </p>
              <a href="mailto:Contact@harwellglobal.com" data-testid="contact-email-link" className="flex gap-3 hover:text-[#1E40AF] transition-colors">
                <Mail className="w-5 h-5 text-[#1E40AF] shrink-0" /> Contact@harwellglobal.com
              </a>
              {["+91-120-4615329", "+91-9936706961", "+91-9289537180"].map((p) => (
                <a key={p} href={`tel:${p.replace(/-/g, "")}`} onClick={() => track("phone_click", { number: p })}
                  data-testid={`contact-phone-${p}`} className="flex gap-3 hover:text-[#1E40AF] transition-colors">
                  <Phone className="w-5 h-5 text-[#1E40AF] shrink-0" /> {p}
                </a>
              ))}
            </div>
            <div className="mt-8 rounded-2xl overflow-hidden border border-[#E6EBF1] shadow-sm">
              <iframe title="Harwell Global Education — Sector 63, Noida"
                src="https://www.google.com/maps?q=B+Block+Sector+63+Noida+Uttar+Pradesh+201301&output=embed"
                className="w-full h-64 border-0" loading="lazy" data-testid="contact-map" />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="bg-[#F7F9FC] rounded-2xl border border-[#E6EBF1] p-7 sm:p-9 shadow-sm">
            <ContactForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- PAGE ---------------- */

export default function Home() {
  return (
    <>
      <Hero />
      <AssessmentSection />
      <TrustStrip />
      <Marquee />
      <Destinations />
      <WhyEurope />
      <FeaturedDestinations />
      <RomaniaFeature />
      <CourseFinderTeaser />
      <Universities />
      <WhyHarwell />
      <Journey />
      <ScholarshipsTeaser />
      <TestPrepTeaser />
      <CostCalcTeaser />
      <Stories />
      <EventsPreview />
      <Parents />
      <FAQ />
      <Insights />
      <FinalCTA />
      <Contact />
    </>
  );
}

export { ProgramExplorer, FAQ, Contact };

/* ---------------- PROGRAM EXPLORER (standalone /programs) ---------------- */

const FIELDS = ["Medicine", "Engineering", "Computer Science", "Business & Management", "Architecture", "Life Sciences", "Social Sciences", "Finance", "International Relations", "Data Science", "Other"];

function ProgramExplorer({ compact = false }) {
  const [programs, setPrograms] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("");
  const [field, setField] = useState("");

  useEffect(() => { api.get("/content/programs").then((r) => setPrograms(r.data)).catch(() => {}); }, []);

  const filtered = programs.filter((p) =>
    (!q || `${p.name} ${p.university} ${p.field}`.toLowerCase().includes(q.toLowerCase())) &&
    (!level || p.degree === level) && (!field || p.field === field)
  );

  const viewProgram = (p) => {
    sessionStorage.setItem("prefill_course", `${p.name} — ${p.university}`);
    track("program_enquiry", { program: p.name, university: p.university });
    window.dispatchEvent(new CustomEvent("prefill-course", { detail: `${p.name} — ${p.university}` }));
    window.location.href = "/contact";
  };

  return (
    <section id="programs" data-testid="program-explorer-section" className={compact ? "" : "py-16 lg:py-20"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!compact && (
          <SectionHead overline="Program Explorer" title="FIND THE RIGHT PROGRAM FOR YOU"
            sub="Filter live programs by study level and field. Every option is matched to your academic profile." />
        )}
        <Reveal className="flex flex-col md:flex-row gap-3 mb-10">
          <input data-testid="program-filter-search-input" placeholder="Search programs, universities, fields..."
            value={q} onChange={(e) => setQ(e.target.value)}
            className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30 focus:border-[#1E40AF]" />
          <select data-testid="program-filter-level-select" value={level} onChange={(e) => setLevel(e.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30">
            <option value="">All Study Levels</option>
            <option>Bachelor's</option>
            <option>Master's</option>
          </select>
          <select data-testid="program-filter-discipline-select" value={field} onChange={(e) => setField(e.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30">
            <option value="">All Fields</option>
            {FIELDS.map((f) => <option key={f}>{f}</option>)}
          </select>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.07 }} data-testid={`program-card-${p.id}`}
              className="bg-white rounded-2xl border border-[#E6EBF1] p-7 shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 hover:-translate-y-1.5 transition-all duration-300 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] font-data font-semibold uppercase tracking-widest text-[#1E40AF] bg-[#EEF5FF] px-2.5 py-1 rounded-full">{p.degree}</span>
                <span className="text-[11px] font-data text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>
              </div>
              <h3 className="text-lg font-bold text-[#172033] leading-snug">{p.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{p.university}</p>
              <div className="mt-5 space-y-1.5 text-xs font-data text-slate-500 flex-1">
                <p>FIELD — <span className="text-slate-700">{p.field}</span></p>
                <p>LANGUAGE — <span className="text-slate-700">{p.language}</span></p>
                <p>DURATION — <span className="text-slate-700">{p.duration}</span></p>
                {p.tuition_range && <p>TUITION — <span className="text-[#92400E]">{p.tuition_range}</span></p>}
              </div>
              <button onClick={() => viewProgram(p)} data-testid={`program-card-apply-btn-${p.id}`}
                className="mt-6 w-full py-2.5 rounded-xl text-[13px] font-bold text-[#1E40AF] border border-[#1E40AF]/30 hover:bg-[#1E40AF] hover:text-white transition-all">
                VIEW PROGRAM
              </button>
            </motion.div>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-center text-slate-500 py-12" data-testid="program-empty-state">No programs match your filters.</p>}
        <p className="text-center text-xs text-slate-400 mt-10 max-w-2xl mx-auto">
          Tuition ranges shown are indicative and subject to each university's latest official information.
        </p>
      </div>
    </section>
  );
}
